// Battery color thresholds shared across all GPX client apps (web + mobile).
// Boundaries (inclusive):
//   null/undefined → unknown (no badge)
//   0              → empty (gray)
//   1–25           → low (red)
//   26–75          → medium (yellow)
//   76–100         → high (green)
export const BATTERY_TIER_THRESHOLDS = {
    low: 25,
    high: 75,
} as const;

export type BatteryTier = 'unknown' | 'empty' | 'low' | 'medium' | 'high';

export function getBatteryTier(pct: number | null | undefined): BatteryTier {
    if (pct === null || pct === undefined) return 'unknown';
    if (pct <= 0) return 'empty';
    if (pct <= BATTERY_TIER_THRESHOLDS.low) return 'low';
    if (pct <= BATTERY_TIER_THRESHOLDS.high) return 'medium';
    return 'high';
}

// Tailwind class bundles for the common badge / icon styling used in
// `getChargeEl` and `DeviceChargeBadge`. Components can also map the tier
// directly when they need different colors.
export interface BatteryBadgeClasses {
    bg: string;
    text: string;
    icon: string;
}

const BADGE_CLASSES: Record<Exclude<BatteryTier, 'unknown'>, BatteryBadgeClasses> = {
    empty: {
        bg: 'bg-gpx_gray-800',
        text: 'text-gpx_gray-100',
        icon: 'text-gpx_gray-200',
    },
    low: {
        bg: 'bg-gpx_red-300',
        text: 'text-gpx_red-100',
        icon: 'text-gpx_red-200',
    },
    medium: {
        bg: 'bg-gpx_yellow-300',
        text: 'text-gpx_yellow-100',
        icon: 'text-gpx_yellow-200',
    },
    high: {
        bg: 'bg-gpx_green-300',
        text: 'text-gpx_green-100',
        icon: 'text-gpx_green-200',
    },
};

export function getBatteryBadgeClasses(
    pct: number | null | undefined,
): BatteryBadgeClasses | null {
    const tier = getBatteryTier(pct);
    if (tier === 'unknown') return null;
    return BADGE_CLASSES[tier];
}
